/**
 * The selected goal in detail. Left: the change in big type, the confidence
 * sentence and the likely-range bar. Right (running tests): whether it can be
 * called, the earliest call date and a checklist of what the call needs; only
 * the open sample-size step gets a progress bar. Then every version against
 * Control, and the change over time (launch goal: the only goal with a daily
 * series). The answer is the view's one orange, and only when callable:
 * until then the number, the bar, the chart line and the Call button stay
 * ink or muted (Paper "Experiments · Results · Goals list · polish").
 */

import { HugeiconsIcon } from "@hugeicons/react"
import { Clock01Icon, Tick02Icon } from "@hugeicons/core-free-icons"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import { formatNumeric } from "@/lib/utils/stringUtils"
import { formatChance, formatRange } from "@/lib/experiments/bayes"
import {
  formatChange,
  formatGoalRate,
  formatMoney,
  goalAnswer,
  type GoalComparison,
  type GoalMetricResult,
} from "@/lib/experiments/goalMetric"
import type { CallGate } from "@/lib/experiments/resultsStage"
import { Hint, LikelyRangeBar, RESULT_TIPS } from "@/components/experiments/results/ResultRows"
import { LiftChart } from "@/components/experiments/results/LiftChart"

const TH = "pb-2 text-xs font-normal leading-4 text-ui-meta"
const TD = "py-2.5 text-[13px] leading-4 tabular-nums"

export type CallStatus = "callable" | "waiting" | "held" | null

type Props = {
  result: GoalMetricResult
  comparison: GoalComparison
  isPrimary: boolean
  loading?: boolean
  settling: { settling: boolean; settlesOn: Date }
  callable: boolean
  /** Running tests only: whether the goal can be called and why not. */
  status: CallStatus
  /** "Dec 13" / "Over a year" while waiting; null when unknown. */
  earliestCall: string | null
  gates: CallGate[]
  readOnly: boolean
  canCall: boolean
  canShip: boolean
  onCall: () => void
  onShip: () => void
  calling?: boolean
  shipping?: boolean
  chart: { timeSeries: Array<Record<string, unknown>> | null; dispersion: number } | null
}

const STATUS_TITLE: Record<Exclude<CallStatus, null>, string> = {
  callable: "Ready to call",
  waiting: "Not callable yet",
  held: "On hold",
}

export function GoalDetailCard({
  result,
  comparison,
  isPrimary,
  loading = false,
  settling,
  callable,
  status,
  earliestCall,
  gates,
  readOnly,
  canCall,
  canShip,
  onCall,
  onShip,
  calling,
  shipping,
  chart,
}: Props) {
  const answer = goalAnswer(result, comparison, { settling, callable })
  const { focus, control, multi } = comparison
  const short = focus?.label.replace(/^Variant\s+/i, "") ?? "B"
  const countHeader = result.unit === "money" ? "Revenue" : result.metric === "count" ? (result.goal?.type === "clicks" || result.noun === "clicks" ? "Clicks" : "Events") : "Converted"
  const confidenceTip = multi ? RESULT_TIPS.confidenceMulti : RESULT_TIPS.confidence
  const showChart = Boolean(result.isLaunch && chart?.timeSeries && control && focus && result.metric !== "descriptive")
  const barDomain: [number, number] = focus?.range
    ? [Math.min(-20, focus.range.lower - 10), Math.max(20, focus.range.upper + 10)]
    : [-20, 20]

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="flex flex-col gap-6 p-5 md:flex-row md:items-stretch">
        {/* The answer */}
        <div className="flex min-w-0 flex-[1.3] flex-col gap-1">
          <span className="text-xs leading-4 text-ui-meta">
            {result.label}
            {isPrimary && " · primary goal"}
          </span>
          {loading ? (
            <Skeleton className="h-[38px] w-36" />
          ) : (
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span
                className={cn(
                  "text-[36px] font-semibold leading-[40px] tracking-[-0.03em] tabular-nums",
                  answer.tone === "answer" ? "text-primary-strong" : answer.tone === "muted" ? "text-muted-foreground" : "text-foreground",
                )}
              >
                {answer.number}
              </span>
              {answer.versus && <span className="text-[13px] leading-[18px] text-muted-foreground">{answer.versus}</span>}
            </div>
          )}
          {!loading && (
            <span className="text-[13px] leading-[19px]">
              <Hint text={confidenceTip}>
                <span className="cursor-help font-medium text-foreground">{answer.label}</span>
              </Hint>
              {answer.detail && <span className="text-muted-foreground"> · {answer.detail}</span>}
            </span>
          )}
          {focus?.range && !loading && result.metric !== "descriptive" && (
            <div className="mt-3 flex max-w-[520px] flex-col gap-1">
              <LikelyRangeBar range={{ ...focus.range, point: focus.change ?? focus.range.point }} domain={barDomain} strong={answer.tone === "answer"} height={16} />
              <span className="text-[11px] leading-[14px] text-ui-meta">Likely range, 95% · the line is no change</span>
            </div>
          )}
        </div>

        {/* What the call needs */}
        {status && !loading && (
          <>
            <div className="hidden w-px bg-border/60 md:block" />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[14px] font-semibold leading-5 text-foreground">{STATUS_TITLE[status]}</span>
                  {status === "waiting" && earliestCall && (
                    <span className="text-[13px] leading-[18px] text-muted-foreground">
                      Earliest call <span className="font-medium text-foreground">{earliestCall}</span>
                    </span>
                  )}
                </div>
                {!readOnly && (
                  <div className="flex shrink-0 items-center gap-2">
                    {canShip && (
                      <Button variant="outline" onClick={onShip} disabled={shipping} className="h-8 rounded-lg px-3 text-xs font-medium">
                        Ship {short}
                      </Button>
                    )}
                    <Button
                      onClick={onCall}
                      disabled={!canCall || calling}
                      className="h-8 rounded-lg px-3 text-xs font-medium disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100"
                    >
                      Call test
                    </Button>
                  </div>
                )}
              </div>
              <ul className="mt-1 flex flex-col gap-2.5" aria-label="What the call needs">
                {gates.map((g) => (
                  <li key={g.key} className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "flex size-[18px] shrink-0 items-center justify-center rounded-full",
                          g.met ? "bg-success-tint text-success" : "text-ui-secondary ring-[1.5px] ring-inset ring-input",
                        )}
                        aria-label={g.met ? "Met" : "Not met yet"}
                      >
                        <HugeiconsIcon icon={g.met ? Tick02Icon : Clock01Icon} size={g.met ? 11 : 10} />
                      </span>
                      <span className="flex-1 text-[13px] leading-[18px] text-foreground">{g.label}</span>
                      <span className="text-[13px] leading-[18px] tabular-nums text-muted-foreground">
                        <span className={cn(!g.met && "font-medium text-foreground")}>{g.now}</span> / {g.needed}
                      </span>
                    </div>
                    {!g.met && g.key === "sample" && (
                      <span className="ml-[26px] h-[5px] overflow-hidden rounded-full bg-muted" role="presentation">
                        <span className="block h-full rounded-full bg-foreground" style={{ width: `${Math.round(Math.min(1, Math.max(0, g.progress)) * 100)}%` }} />
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>

      {/* Every version against Control */}
      <div className="overflow-x-auto px-5 pb-2">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b border-border">
              <th className={cn(TH, "text-left")}>Version</th>
              <th className={cn(TH, "text-right")}>Visitors</th>
              <th className={cn(TH, "text-right")}>{countHeader}</th>
              <th className={cn(TH, "text-right")}>{result.unit === "percent" ? "Rate" : "Per visitor"}</th>
              <th className={cn(TH, "text-right")}>Change</th>
              <th className={cn(TH, "text-right")}>Likely change</th>
              <th className={cn(TH, "text-right")}>{multi ? "Best version" : "Confidence"}</th>
            </tr>
          </thead>
          <tbody>
            {comparison.rows.map((r) => (
              <tr key={r.id} className="border-t border-ui-rowline first:border-t-0">
                <td className={cn(TD, r.id === focus?.id ? "font-semibold" : "font-medium", "text-foreground")}>
                  <span className="flex items-center gap-2">
                    <span
                      className={cn(
                        "flex size-[18px] shrink-0 items-center justify-center rounded-[5px] text-[11px] font-semibold leading-none",
                        r.isControl ? "bg-muted text-ui-secondary" : r.id === focus?.id ? "bg-foreground text-background" : "bg-muted text-foreground",
                      )}
                      aria-hidden
                    >
                      {r.letter}
                    </span>
                    {r.label}
                  </span>
                </td>
                <td className={cn(TD, "text-right text-foreground")}>{loading ? <Skeleton className="ml-auto h-[13px] w-12" /> : formatNumeric(r.exposure)}</td>
                <td className={cn(TD, "text-right text-muted-foreground")}>
                  {loading ? <Skeleton className="ml-auto h-[13px] w-12" /> : !result.available ? "—" : result.unit === "money" ? formatMoney(r.events, result.currency) : formatNumeric(r.events)}
                </td>
                <td className={cn(TD, "text-right text-foreground")}>{result.available && !loading ? formatGoalRate(r.rate, result.unit, result.currency) : "—"}</td>
                <td className={cn(TD, "text-right font-medium text-foreground")}>
                  {r.isControl ? <span className="font-normal text-ui-meta">Baseline</span> : result.available ? formatChange(r.change, r.isNew) : "—"}
                </td>
                <td className={cn(TD, "text-right text-foreground")}>{r.range && result.available && !loading ? formatRange(r.range) : <span className="text-muted-foreground">—</span>}</td>
                <td className={cn(TD, "text-right text-foreground")}>
                  {r.best == null || !result.available || (r.isControl && !multi) ? <span className="text-muted-foreground">—</span> : formatChance(r.best)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showChart && (
        <div className="border-t border-border/60 px-5 pt-4 pb-5">
          <div className="mb-3 flex items-end justify-between gap-3">
            <Hint text={RESULT_TIPS.range}>
              <span className="flex w-fit cursor-help flex-col gap-0.5">
                <span className="text-sm font-medium leading-5 text-foreground">Change vs Control, over time</span>
                <span className="text-xs leading-4 text-ui-meta">Cumulative · {focus!.label}</span>
              </span>
            </Hint>
            <span className="flex items-center gap-3.5 text-xs leading-4 text-ui-secondary">
              <span className="flex items-center gap-1.5">
                <span className={cn("h-0.5 w-3 rounded-full", callable ? "bg-primary" : "bg-foreground")} />
                Change
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 border-t-[1.5px] border-dashed border-ring" />
                Likely range
              </span>
            </span>
          </div>
          <LiftChart
            timeSeries={chart!.timeSeries!}
            controlId={control!.id}
            variantId={focus!.id}
            variantLabel={focus!.label}
            metric={result.metric === "count" ? "count" : "proportion"}
            dispersion={chart!.dispersion}
            callable={callable}
          />
        </div>
      )}
    </Card>
  )
}
